import React, { useState, useEffect, useCallback } from "react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Eye, EyeOff, Users, MousePointerClick, MessageCircle, TrendingUp, LogOut, RefreshCw, Lock, LifeBuoy, MessagesSquare,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#C59B27";
const PIE_COLORS = ["#C59B27", "#1C1917", "#D4A373", "#8C857B", "#57534E"];
const RANGES = [{ d: 7, l: "7 days" }, { d: 30, l: "30 days" }, { d: 90, l: "90 days" }];

function getCsrf() {
  const m = document.cookie.match(/(?:^|;\s*)admin_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

// Cookie-based admin request: always sends the HttpOnly session cookie and, for
// state-changing calls, the double-submit CSRF token.
async function adminFetch(path, opts = {}) {
  const method = (opts.method || "GET").toUpperCase();
  const headers = { ...(opts.headers || {}) };
  if (opts.body) headers["Content-Type"] = "application/json";
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) headers["X-CSRF-Token"] = getCsrf();
  return fetch(`${API}${path}`, { ...opts, method, headers, credentials: "include" });
}

function Stat({ icon: Icon, label, value, suffix }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-2 text-white/50 text-xs font-mono uppercase tracking-widest">
        <Icon className="w-4 h-4 text-stylist-gold" />{label}
      </div>
      <p className="mt-3 font-serif text-4xl text-white tabular-nums">
        {value}<span className="text-stylist-gold text-2xl">{suffix}</span>
      </p>
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid={testid}>
      <h3 className="font-serif text-xl text-white mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Login({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await adminFetch("/admin/login", {
        method: "POST", body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Login failed");
      onAuthed();
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-stylist-ink flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-8" data-testid="admin-login-form">
        <div className="w-12 h-12 rounded-full bg-stylist-gold/15 flex items-center justify-center mb-5">
          <Lock className="w-6 h-6 text-stylist-gold" />
        </div>
        <h1 className="font-serif text-2xl text-white">Super Admin</h1>
        <p className="text-white/50 text-sm mt-1">Senior Stylist analytics</p>
        <input data-testid="admin-email" type="email" placeholder="Email" value={email}
          onChange={(e) => setEmail(e.target.value)} required
          className="mt-6 w-full rounded-lg bg-white/5 border border-white/15 px-4 py-3 text-white text-sm outline-none focus:border-stylist-gold" />
        <div className="relative mt-3">
          <input data-testid="admin-password" type={showPassword ? "text" : "password"} placeholder="Password" value={password}
            onChange={(e) => setPassword(e.target.value)} required
            className="w-full rounded-lg bg-white/5 border border-white/15 px-4 py-3 pr-11 text-white text-sm outline-none focus:border-stylist-gold" />
          <button type="button" data-testid="admin-password-toggle"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-white/40 hover:text-stylist-gold transition-colors">
            {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
          </button>
        </div>
        {err && <p className="mt-3 text-sm text-red-400" data-testid="admin-login-error">{err}</p>}
        <button data-testid="admin-login-submit" disabled={busy}
          className="mt-5 w-full rounded-full bg-stylist-gold text-stylist-ink py-3 text-sm font-medium hover:bg-white transition-colors disabled:opacity-60">
          {busy ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function BrevoPanel() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true); setErr("");
      try {
        const r = await adminFetch("/admin/brevo/summary");
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || "Could not load Brevo");
        setD(j);
      } catch (e) { setErr(e.message); } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <p className="mt-8 text-white/50" data-testid="brevo-loading">Loading Brevo data...</p>;
  if (err) return <p className="mt-8 text-red-400" data-testid="brevo-error">{err}</p>;

  return (
    <div className="mt-8" data-testid="brevo-panel">
      <div className="rounded-2xl border border-stylist-gold/25 bg-stylist-gold/[0.05] p-5 flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-stylist-gold">Connected account</p>
          <p className="font-serif text-xl text-white">{d.company} <span className="text-white/40 text-sm">· {d.email}</span></p>
        </div>
        <span className="text-sm text-white/60">{(d.email_credits || 0).toLocaleString()} email credits</span>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Stat icon={Users} label="Contacts" value={d.total_contacts} />
        <Stat icon={Eye} label="Lists" value={d.total_lists} />
        <Stat icon={MessageCircle} label="Campaigns" value={d.total_campaigns} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Contact lists" testid="brevo-lists">
          <div className="space-y-2">
            {d.lists.map((l) => (
              <div key={l.id} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5">
                <span className="text-white/75">{l.name}</span>
                <span className="font-mono text-stylist-gold">{l.subscribers.toLocaleString()}</span>
              </div>
            ))}
            {d.lists.length === 0 && <p className="text-white/40 text-sm">No lists yet</p>}
          </div>
        </Panel>
        <Panel title="Recent campaigns" testid="brevo-campaigns">
          <div className="space-y-3">
            {d.campaigns.map((c) => (
              <div key={c.id} className="text-sm border-b border-white/5 pb-2">
                <p className="text-white/80 truncate">{c.name}</p>
                <div className="flex gap-4 mt-1 text-xs text-white/50 font-mono">
                  <span>Sent {c.sent}</span><span>Opens {c.opens}</span><span>Clicks {c.clicks}</span>
                </div>
              </div>
            ))}
            {d.campaigns.length === 0 && <p className="text-white/40 text-sm">No campaigns yet</p>}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function ChloePanel() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await adminFetch("/admin/chloe/report?days=14");
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Could not load report");
      setD(j);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="mt-8 text-white/50" data-testid="chloe-loading">Loading Chloe report...</p>;
  if (err) return <p className="mt-8 text-red-400" data-testid="chloe-error">{err}</p>;

  const today = d?.today || { people: 0, messages: 0, referrals: 0, referral_questions: [] };
  const daily = (d?.daily || []).slice().reverse();
  const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div className="mt-8" data-testid="chloe-panel">
      <div className="rounded-2xl border border-stylist-gold/25 bg-stylist-gold/[0.05] p-5 mb-6">
        <p className="text-[10px] font-mono uppercase tracking-widest text-stylist-gold">Chloe daily report</p>
        <p className="font-serif text-xl text-white">Today, {today.date}</p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Stat icon={Users} label="People today" value={today.people} />
        <Stat icon={MessagesSquare} label="Questions asked" value={today.messages} />
        <Stat icon={LifeBuoy} label="Sent to support" value={today.referrals} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Questions Chloe sent to support today" testid="chloe-referrals">
          <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
            {today.referral_questions.map((q, i) => (
              <div key={i} className="text-sm border-b border-white/5 pb-2" data-testid={`referral-${i}`}>
                <p className="text-white/85">{q.question}</p>
                <p className="text-xs text-white/40 font-mono mt-1">{fmtTime(q.created_at)}</p>
              </div>
            ))}
            {today.referral_questions.length === 0 && (
              <p className="text-white/40 text-sm">No questions were sent to support today. Chloe answered everything.</p>
            )}
          </div>
        </Panel>

        <Panel title="Last 14 days" testid="chloe-daily">
          <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1">
            <div className="grid grid-cols-4 text-[10px] font-mono uppercase tracking-widest text-white/40 pb-2 border-b border-white/10">
              <span>Date</span><span className="text-right">People</span><span className="text-right">Questions</span><span className="text-right">To support</span>
            </div>
            {daily.map((row) => (
              <div key={row.date} className="grid grid-cols-4 text-sm py-1.5 border-b border-white/5">
                <span className="text-white/70">{row.date}</span>
                <span className="text-right font-mono text-white">{row.people}</span>
                <span className="text-right font-mono text-white/70">{row.messages}</span>
                <span className="text-right font-mono text-stylist-gold">{row.referrals}</span>
              </div>
            ))}
            {daily.length === 0 && <p className="text-white/40 text-sm">No activity yet</p>}
          </div>
        </Panel>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [authed, setAuthed] = useState(null); // null = checking, true, false
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState("analytics");

  const load = useCallback(async (d = days) => {
    setLoading(true); setError("");
    try {
      const r = await adminFetch(`/admin/analytics/summary?days=${d}`);
      if (r.status === 401 || r.status === 403) { setAuthed(false); return; }
      const j = await r.json();
      setData(j);
    } catch (e) { setError("Could not load analytics"); } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { document.title = "Super Admin | Senior Stylist"; }, []);
  // Check for an existing session cookie on mount.
  useEffect(() => {
    (async () => {
      try {
        const r = await adminFetch("/admin/me");
        setAuthed(r.ok);
      } catch { setAuthed(false); }
    })();
  }, []);
  useEffect(() => { if (authed) load(days); }, [authed, days, load]);

  if (authed === null) {
    return <div className="min-h-screen bg-stylist-ink flex items-center justify-center text-white/50" data-testid="admin-checking">Loading...</div>;
  }
  if (!authed) return <Login onAuthed={() => setAuthed(true)} />;

  const t = data?.totals || {};
  const logout = async () => {
    try { await adminFetch("/admin/logout", { method: "POST" }); } catch { /* ignore */ }
    setAuthed(false);
  };

  return (
    <div className="min-h-screen bg-stylist-ink text-white" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">Super Admin</span>
            <h1 className="font-serif text-3xl sm:text-4xl mt-1">Analytics Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-full border border-white/15 overflow-hidden">
              {RANGES.map((r) => (
                <button key={r.d} data-testid={`range-${r.d}`} onClick={() => setDays(r.d)}
                  className={"px-4 py-2 text-xs " + (days === r.d ? "bg-stylist-gold text-stylist-ink" : "text-white/60 hover:text-white")}>
                  {r.l}
                </button>
              ))}
            </div>
            <button onClick={() => load(days)} data-testid="refresh-btn" className="p-2.5 rounded-full border border-white/15 text-white/70 hover:text-white">
              <RefreshCw className={"w-4 h-4 " + (loading ? "animate-spin" : "")} />
            </button>
            <button onClick={logout} data-testid="admin-logout" className="p-2.5 rounded-full border border-white/15 text-white/70 hover:text-white">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {error && <p className="mt-6 text-red-400 text-sm">{error}</p>}

        <div className="mt-6 flex gap-2">
          <button onClick={() => setView("analytics")} data-testid="tab-analytics"
            className={"px-5 py-2 rounded-full text-sm " + (view === "analytics" ? "bg-stylist-gold text-stylist-ink" : "border border-white/15 text-white/60 hover:text-white")}>
            Website analytics
          </button>
          <button onClick={() => setView("brevo")} data-testid="tab-brevo"
            className={"px-5 py-2 rounded-full text-sm " + (view === "brevo" ? "bg-stylist-gold text-stylist-ink" : "border border-white/15 text-white/60 hover:text-white")}>
            Outreach (Brevo)
          </button>
          <button onClick={() => setView("chloe")} data-testid="tab-chloe"
            className={"px-5 py-2 rounded-full text-sm " + (view === "chloe" ? "bg-stylist-gold text-stylist-ink" : "border border-white/15 text-white/60 hover:text-white")}>
            Chloe report
          </button>
        </div>

        {view === "brevo" && <BrevoPanel />}
        {view === "chloe" && <ChloePanel />}

        {view === "analytics" && (<>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat icon={Eye} label="Page views" value={t.pageviews ?? 0} />
          <Stat icon={Users} label="Visitors" value={t.visitors ?? 0} />
          <Stat icon={MousePointerClick} label="CTA clicks" value={t.cta_clicks ?? 0} />
          <Stat icon={MessageCircle} label="Chat opens" value={t.chatbot_opens ?? 0} />
          <Stat icon={TrendingUp} label="Conversion" value={t.conversion ?? 0} suffix="%" />
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Panel title="Traffic over time" testid="chart-timeseries">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data?.timeseries || []}>
                  <CartesianGrid stroke="#ffffff12" vertical={false} />
                  <XAxis dataKey="date" stroke="#8C857B" fontSize={11} />
                  <YAxis stroke="#8C857B" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#141416", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} />
                  <Line type="monotone" dataKey="pageviews" stroke={GOLD} strokeWidth={2.5} dot={false} name="Page views" />
                  <Line type="monotone" dataKey="visitors" stroke="#D4A373" strokeWidth={2} dot={false} name="Visitors" />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          </div>
          <Panel title="Devices" testid="chart-devices">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data?.devices || []} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={95} label>
                  {(data?.devices || []).map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#141416", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          </Panel>
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <Panel title="Top pages" testid="chart-pages">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data?.top_pages || []} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" stroke="#8C857B" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#8C857B" fontSize={10} width={90} />
                <Tooltip contentStyle={{ background: "#141416", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="count" fill={GOLD} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Referrers" testid="chart-referrers">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data?.referrers || []} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" stroke="#8C857B" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#8C857B" fontSize={10} width={90} />
                <Tooltip contentStyle={{ background: "#141416", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="count" fill="#D4A373" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Countries" testid="chart-countries">
            <div className="space-y-3">
              {(data?.countries || []).map((c) => (
                <div key={c.name} className="flex items-center justify-between text-sm">
                  <span className="text-white/70">{c.name}</span>
                  <span className="font-mono text-stylist-gold">{c.count}</span>
                </div>
              ))}
              {(!data?.countries || data.countries.length === 0) && <p className="text-white/40 text-sm">No data yet</p>}
            </div>
          </Panel>
        </div>
        </>)}
      </div>
    </div>
  );
}
